(ns rlws.iot-cloud.frontend.forms
  (:require [clojure.string :as str]
            [fork.re-frame :as fork]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [rlws.iot-cloud.frontend.site :as site]
            [rlws.iot-cloud.frontend.events :as events]
            [rlws.iot-cloud.frontend.openapi :as openapi]
            [rlws.iot-cloud.frontend.subscriptions :as sub]
            [rlws.iot-cloud.validation :as validation]
            [tick.alpha.api :as t]
            [vlad.core :as vlad]
            [potpuri.core :as potpuri]))

(defn form->model
  [values]
  (into {}
        (map (fn [{:strs [label value]}]
               {(keyword (-> label
                             (str/replace #"\s+" " ")
                             (str/trim)
                             (str/lower-case)
                             (str/replace #"\s" "-")))
                {:label label
                 :value value}}) values)))

(defn model->form
  [values]
  (mapv
   (fn [[k {:keys [label value]}]]
     {"label" label
      "value" value})
   values))

(defn fork-input
  "Takes props from fork and a map of options. If a type option is passed this is
  used as the input type as well as the form key. If no type option is passed a
  key option must be passed and the input will be assumed to be a text input"
  [{:keys [values
           normalize-name
           handle-change
           handle-blur
           touched
           errors]}
   {:keys [type name] :as options}]
  (let [name (or name type)]
    [:<>
     [:input
      (merge
       {:id name
        :class "input"
        :name (normalize-name name)
        :type "text"
        :value (get values name)
        :on-change handle-change
        :on-blur handle-blur}
       (dissoc options :name))]
     (when (touched name)
       [:div.form__error (first (get errors (list name)))])]))

(defn fork-textarea
  [{:keys [values
           normalize-name
           handle-change
           handle-blur
           touched
           errors]}
   {:keys [type name] :as options}]
  (let [name (or name type)]
    [:<>
     [:textarea
      (merge
       {:id type
        :class "input"
        :name (normalize-name name)
        :type "text"
        :value (get values name)
        :on-change handle-change
        :on-blur handle-blur}
       (dissoc options :name))]
     (when (touched name)
       [:div.form__error (first (get errors (list name)))])]))

(defn select
  [component & [args]]
  (let [!ref-button (atom nil)
        !ref-items (atom nil)
        active? (r/atom false)
        handler (fn [e]
                  (let [^js node (.-target e)]
                    (if (.contains @!ref-button node)
                      (swap! active? not)
                      (reset! active? false))))
        ref-button (fn [el] (reset! !ref-button el))
        ref-items (fn [el] (reset! !ref-items el))]
    (r/create-class
     {:component-did-mount
      (fn []
        (js/document.addEventListener "mouseup" handler))
      :component-will-unmount
      (fn []
        (js/document.removeEventListener "mouseup" handler))
      :reagent-render
      (fn [component]
        [component {:active? @active?
                    :ref-button ref-button
                    :ref-items ref-items
                    :args args}])})))

(defn field-array-fn
  [{:keys [state values normalize-name]}
   {:fieldarray/keys [name
                      options
                      fields
                      insert
                      remove
                      set-handle-change
                      set-handle-blur
                      handle-change
                      handle-blur]}]
  (let [inputs (:inputs options)
        fieldarray-name name]
    [:<>
     (map-indexed
      (fn [idx field-map]
        ^{:key idx}
        [:div.fieldarray
         [:div.fieldarray__group
          (for [{:keys [name label type render-fn] :as input} inputs
                :let [render-fn (or render-fn identity)]]
            ^{:key (str name)}
            [:div.form__group
             [:label.form-label.form__label label]
             (case type
               :field [:div.form-control-wrapper
                       [:input.form-control.input-control
                        {:type "text"
                         :name (normalize-name name)
                         :value (get field-map name)
                         :on-change #(handle-change % idx)
                         :on-blur #(handle-blur % idx)}]]

               :dropdown [select
                          (fn [{:keys [active? ref-button ref-items]}]
                            [:div.fieldarray__dropdown-group
                             [:button.btn.btn--md.fieldarray__dropdown
                              {:type "button"
                               :ref ref-button}
                              [:div.flex
                               (if-let [v (not-empty (get-in values [fieldarray-name idx name]))]
                                 (render-fn v)
                                 "Choose one")
                               [:i.fas.fa-caret-down.margin-left-xxs]]]
                             [:div.select.fieldarray__dropdown-select
                              {:ref ref-items
                               :class (if active?
                                        "select--show"
                                        "select--hide")}
                              (for [[k v] (:dropdown-options input)]
                                ^{:key (str k v)}
                                [:div.option
                                 {:on-click #(do
                                               (set-handle-change
                                                {:value v
                                                 :path [fieldarray-name idx name]})
                                               (set-handle-blur
                                                {:value true
                                                 :path [fieldarray-name idx name]}))}
                                 (render-fn v)])]])]
               [:div "field array type not found"])])]
         [:button.btn.fieldarray__remove
          {:type "button"
           :disabled (= (:min options) (count fields))
           :on-click #(remove idx)}
          [:i.fas.fa-times]]])
      fields)
     (when-not (= (:max options) (count fields))
       [:button.btn.fieldarray__add
        {:type "button"
         :on-click #(insert
                     (zipmap (mapv (fn [x] (get x :name)) inputs)
                             (repeat "")))}
        [:i.fas.fa-plus]
        [:p "Add"]])]))

(defn form-component
  [{:keys [model fork submit delete]}]
  [fork/form (merge
              {:keywordize-keys true
               :prevent-default? true
               :clean-on-unmount? true}
              fork)
   (fn [{:keys [values
                path
                state
                errors
                submitting?
                attempted-submissions
                on-submit-server-message
                handle-submit
                set-handle-change
                set-handle-blur
                reset] :as props}]
     [:div.form
      [:form.profile-component__form
       {:on-submit handle-submit}
       (for [{:keys [type] :as info} model]
         ^{:key info}
         [:<>
          (case type
            :field [:div.form__group
                    [:label.form-label.form__label
                     (:label info)]
                    [:div.form-control-wrapper
                     [fork-input props
                      (merge
                       {:class "form-control input-control"
                        :name (:name info)}
                       (:options info))]]]

            :textarea [:div.form__group
                       [:label.form-label.form__label
                        (:label info)]
                       [:div.form-control-wrapper
                        [fork-textarea props
                         (merge
                          {:class "form-control input-control"
                           :name (:name info)}
                          (:options info))]]]

            :field-array [:div.form__group
                          [:p.form__section-title (:label info)]
                          [fork/field-array
                           {:props props
                            :name (:name info)
                            :options (:options info)}
                           field-array-fn]]

            :dropdown [select
                       (fn [{:keys [active? ref-button ref-items]}]
                         [:div.form__group
                          [:p.form__section-title (:label info)]
                          [:div.dropdown-button-delete__group
                           [:div.form__dropdown-group
                            [:button.btn.btn--md.form__dropdown-button
                             {:type "button"
                              :ref ref-button}
                             (let [opts (into {} (:dropdown-options info))]
                               [:div.form__dropdown-selected-item
                                (if-let [k (get values (:name info))]
                                  [:div (get opts k)]
                                  [:div (get-in info [:options :placeholder] "Choose one")])
                                [:div [:i.fas.fa-caret-down.margin-left-xxs]]])]
                            [:div.select.form__dropdown-select
                             {:ref ref-items
                              :class (if active?
                                       "select--show"
                                       "select--hide")}
                             (for [[k v] (:dropdown-options info)]
                               ^{:key (str k v)}
                               [:div.option
                                {:on-click #(do
                                              (set-handle-change
                                               {:value k
                                                :path [(:name info)]})
                                              (set-handle-blur
                                               {:value true
                                                :path [(:name info)]}))}
                                v])]]
                           (when-not (get info :required?)
                             [:button.btn.dropdown-button__remove
                              {:type "button"
                               :disabled (not (get values (:name info)))
                               :on-click #(do
                                            (set-handle-change
                                             {:value nil
                                              :path [(:name info)]})
                                            (set-handle-blur
                                             {:value true
                                              :path [(:name info)]}))}
                              [:i.fas.fa-times]])]])]
            nil)])
       [:div.form__buttons-group
        [:button.btn.btn--md.form__submit
         {:type "submit"
          :disabled (or submitting?
                        (and errors (> attempted-submissions 0)))}
         (or (:label submit) "Submit Form")]
        (when delete
          [:div
           [:button.btn.btn--md.form__delete
            {:type "button"
             :on-click #((:on-click delete) {:values values
                                             :path path})}
            (:label delete)]])]
       (when on-submit-server-message
         [:div.form__submit-error
          on-submit-server-message])]])])

(def asset-component-basic
  [{:type :field
    :label "Name"
    :name :asset/name
    :options {}}
   {:type :field
    :label "Category"
    :name :asset/category
    :options {}}
   {:type :field
    :label "Address"
    :name :asset/address
    :options {}}
   {:type :field-array
    :label "Images:"
    :name :asset/images
    :options {:inputs [{:name :url
                        :label "url"
                        :type :field}]}}])

(defn edit-asset-basic
  [values]
  {:fork {:initial-values values
          :on-submit #(rf/dispatch [::events/edit-asset %])
          :validation #(vlad/field-errors validation/asset-profile-base-validation %)}
   :submit {:label "Update Asset"}
   :model asset-component-basic})

(def asset-component-model
  [{:type :field
    :label "Name"
    :name :name
    :options {}}
   {:type :field-array
    :label "Details:"
    :name :info-group
    :options {:min 1
              :inputs [{:name :icon
                        :label "icon"
                        :type :dropdown
                        :render-fn (fn [v] [:i {:class v}])
                        :dropdown-options [[:tag "fas fa-tag"]
                                           [:scale "fas fa-balance-scale"]
                                           [:layer "fas fa-layer-group"]
                                           [:building "fas fa-building"]
                                           [:book "fas fa-address-book"]
                                           [:cubes "fas fa-cubes"]
                                           [:industry "fas fa-industry"]
                                           [:info "fas fa-info"]
                                           [:badge "fas fa-id-badge"]
                                           [:desktop "fas fa-desktop"]
                                           [:ethernet "fas fa-ethernet"]
                                           [:fingerprint "fas fa-fingerprint"]]}
                       {:name :label
                        :label "label"
                        :type :field}
                       {:name :value
                        :label "value"
                        :type :field}]}}
   {:type :field-array
    :label "Images:"
    :name :images
    :options {:inputs [{:name :url
                        :label "url"
                        :type :field}]}}])

(defn add-asset-component
  [id]
  {:fork {:initial-values {:id id
                           :component-type :general
                           :info-group [{:label ""
                                         :value ""
                                         :icon ""}]}
          :on-submit #(rf/dispatch [::events/edit-asset %])
          :validation #(vlad/field-errors validation/asset-profile-components-validation %)}
   :submit {:label "Create Component"}
   :model asset-component-model})

(defn edit-asset-component
  [values]
  {:fork {:initial-values values
          :on-submit #(rf/dispatch [::events/edit-asset %])
          :validation #(vlad/field-errors validation/asset-profile-components-validation %)}
   :submit {:label "Update Component"}
   :model asset-component-model
   :delete {:label "Delete Component"
            :on-click #(rf/dispatch [::events/delete-asset-component %])}})

(def asset-scale-model
  (conj asset-component-model
        {:type :field-array
         :label "Scale:"
         :name :scale
         :options {:min 1
                   :max 1
                   :inputs (mapv (fn [x]
                                   {:name (keyword (str "cell" x))
                                    :type :field
                                    :label (str "Cell " x)})
                                 (range 1 17))}}))

(defn edit-asset-scale
  [values]
  {:fork {:initial-values values
          :on-submit #(rf/dispatch [::events/edit-asset %])
          :validation #(vlad/field-errors validation/asset-profile-components-validation %)}
   :submit {:label "Update Scale"}
   :model asset-scale-model})

(def service-notes-model
  [{:type :field
    :label "Title"
    :name :title}
   {:type :field
    :label "Type"
    :name :note-type}
   {:type :dropdown
    :label "Cell"
    :name :cell
    :required? false
    :dropdown-options [["Cell 1" "Cell 1"]
                       ["Cell 2" "Cell 2"]
                       ["Cell 3" "Cell 3"]
                       ["Cell 4" "Cell 4"]
                       ["Cell 5" "Cell 5"]
                       ["Cell 6" "Cell 6"]
                       ["Cell 7" "Cell 7"]
                       ["Cell 8" "Cell 8"]
                       ["Cell 9" "Cell 9"]
                       ["Cell 10" "Cell 10"]
                       ["Cell 11" "Cell 11"]
                       ["Cell 12" "Cell 12"]
                       ["Cell 13" "Cell 13"]
                       ["Cell 14" "Cell 14"]
                       ["Cell 15" "Cell 15"]
                       ["Cell 16" "Cell 16"]]}
   {:type :textarea
    :label "Note"
    :name :body}])

(defn create-asset-note []
  {:fork {:on-submit #(rf/dispatch [::events/add-service-note %])}
   :submit {:label "Add Note"}
   :model service-notes-model})

(defn edit-asset-note [values]
  {:fork {:on-submit #(rf/dispatch [::events/edit-service-note %])
          :initial-values values}
   :submit {:label "Edit Note"}
   :model service-notes-model
   :delete {:label "Delete Note"
            :on-click #(rf/dispatch [::events/delete-service-note %])}})

(defn create-document-link
  []
  (let [state (r/atom {"note-type" "fixed"})]
    (fn []
      (let [openapi @(rf/subscribe [::sub/data :openapi])
            asset (:raw-data @(rf/subscribe [::sub/current-asset]))]
        [:div
         (if openapi
           [openapi/form
            {:openapi openapi
             :schema-name "DocumentLink"
             :form-data @state
             :remap-properties
             {"asset" (constantly {"title" "Asset ID"
                                   "type" "string"
                                   "default" (site/to-plain-id asset)})}
             :on-submit (fn [data]
                          (let [id (random-uuid)]
                            (rf/dispatch [::events/put-data
                                          (str "/api/documents/" id)
                                          data
                                          [::events/generic-modal-success "Added Document Link"]])))}
            [:div [:button.btn.btn--action {:type :submit} "Add Document"]]]
           [:p "Loading..."])]))))

(defn create-service-note
  ([] (create-service-note {"note-type" "fixed"}))
  ([init-state]
   (let [state (r/atom init-state)
         submitting? (r/atom false)]
     (fn [init-state]
       (let [openapi @(rf/subscribe [::sub/data :openapi])
             {:keys [dealership] :as asset} (:raw-data @(rf/subscribe [::sub/current-asset]))
             current-user @(rf/subscribe [::sub/current-user])
             users @(rf/subscribe [::sub/users])
             service-personnel
             (filter (fn [user]
                       (and (= dealership (:dealership user))
                            (condp #(contains? %2 %1) (set (:roles user))
                              "service-technician" true
                              "service-engineer" true
                              false)))
                     users)
             cells (conj (filter (potpuri/where-fn {:component-type "cell"})
                                 (:components asset))
                         {:cell-number 0
                          :component-type "cell"})]
         [:div
          (if openapi
            [openapi/form
             {:openapi openapi
              :class "rjsf modal-form form--uniform-fields"
              :schema-name "ServiceNote"
              :form-data @state
              :remap-properties
              {"timestamp" nil
               "created-by" nil
               "asset" (constantly {"title" "Asset ID"
                                    "type" "string"
                                    "default" (site/to-plain-id asset)})
               "assignee" (when (not-empty service-personnel)
                            #(openapi/create-enum %
                                                  service-personnel
                                                  {:default? false}))}
              :on-submit (fn [data]
                           (let [id (random-uuid)]
                             (rf/dispatch [::events/add-service-note
                                           (assoc data
                                                  :timestamp (t/now)
                                                  :created-by (:username current-user))])
                             (reset! submitting? true)))}
             [:div [:button.btn.btn--action
                    {:type :submit
                     :disabled @submitting?
                     :class (when @submitting? nil)}
                    "Add Service Note"]]]
            [:p "Loading..."])])))))
